import { MapLocation, GeocodingResult } from '../types/maps';

// Set Times Square as default location for maps
export const DEFAULT_LOCATION: MapLocation = {
  lat: 40.7580,
  lng: -73.9855,
  address: 'New York, NY, USA'
};

/**
 * Convert coordinates to a readable address using OpenStreetMap Nominatim
 * Free service, no API key required
 */
export const reverseGeocode = async (
  lat: number, 
  lng: number
): Promise<GeocodingResult | null> => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'ReportThisMF/1.0' // Required by Nominatim terms of service
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.address) {
      return null;
    }

    const address = data.address;
    
    // Extract address components
    const components: GeocodingResult['components'] = {};
    
    if (address.house_number) components.street_number = address.house_number;
    if (address.road) components.route = address.road;
    if (address.city) components.locality = address.city;
    if (address.state) components.administrative_area_level_1 = address.state;
    if (address.postcode) components.postal_code = address.postcode;
    if (address.country) components.country = address.country;

    // Build formatted address
    const streetAddress = [
      components.street_number,
      components.route
    ].filter(Boolean).join(' ');

    const cityState = [
      components.locality,
      components.administrative_area_level_1
    ].filter(Boolean).join(', ');

    const formattedAddress = [
      streetAddress,
      cityState,
      components.postal_code
    ].filter(Boolean).join(', ');

    return {
      address: data.display_name,
      formattedAddress: formattedAddress || data.display_name,
      components
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

/**
 * Convert address to coordinates using OpenStreetMap Nominatim
 * Free service, no API key required
 */
export const geocodeAddress = async (
  address: string
): Promise<MapLocation | null> => {
  try {
    const encodedAddress = encodeURIComponent(address);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`,
      {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'ReportThisMF/1.0' // Required by Nominatim terms of service
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.length === 0) {
      return null;
    }

    const result = data[0];

    return {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      address: result.display_name
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

/**
 * Format coordinates for display
 */
export const formatCoordinates = (lat: number, lng: number): string => {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

/**
 * Calculate distance between two points in meters
 */
export const calculateDistance = (
  lat1: number, 
  lng1: number, 
  lat2: number, 
  lng2: number
): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}; 